"use client";

import { StaticImageData } from "next/image";
import { MouseEvent, PointerEvent, useEffect, useRef, useState } from "react";

import { CarCardImage } from "./CarCardImage";
import IconArrowRight from "../Icons/IconArrowRight";
import { DEFAULT_PLACEHOLDER } from "@/utils/constants";

interface CarCardSliderProps {
  images: { mediaUrl?: string | StaticImageData }[];
  makeModel: string;
  priority: boolean;
}

const DRAG_START_PX = 5;
const DRAG_CHANGE_RATIO = 0.15; // share of a slide a mouse drag must cover to move to the next photo

// Hidden on touch screens (they swipe); on desktop shown on hover and on keyboard focus.
const arrowClassName =
  "absolute right-3 z-50 hidden h-8 w-8 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-white transition-opacity duration-300 ease-in-out md:flex md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100";

/**
 * Native scroll-snap photo slider for car cards. Slide 1 is the server-rendered photo (the page's LCP element)
 * and is never replaced. Touch uses native scrolling; desktop adds arrows and mouse drag.
 */
export function CarCardSlider({ images, makeModel, priority }: CarCardSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  const [current, setCurrent] = useState(0);
  const [loadNeighbours, setLoadNeighbours] = useState(false);
  const [announce, setAnnounce] = useState(false);

  const total = images.length;
  // Like Swiper's virtual + loop modes: only the current photo and its neighbours get an <img>, so 20 cards don't
  // hold ~280 images. Every slide box always exists, so the track width never changes while scrolling (RTL-safe).
  const hasImage = (index: number) =>
    index === 0 ||
    (loadNeighbours &&
      (index === current || index === (current + 1) % total || index === (current - 1 + total) % total));

  // Neighbour photos download after page load (or on first scroll), so they never compete with the first photo.
  useEffect(() => {
    const handleLoad = () => setLoadNeighbours(true);
    if (document.readyState === "complete") {
      handleLoad();
      return;
    }
    window.addEventListener("load", handleLoad, { once: true });
    return () => window.removeEventListener("load", handleLoad);
  }, []);

  const getSlideWidth = () => trackRef.current!.getBoundingClientRect().width;

  const scrollToIndex = (index: number, instant = false) => {
    const track = trackRef.current!;
    const direction = getComputedStyle(track).direction === "rtl" ? -1 : 1;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollTo({
      left: direction * index * getSlideWidth(),
      behavior: instant || reduceMotion ? "auto" : "smooth",
    });
  };

  const handleScroll = () => {
    setLoadNeighbours(true);
    setCurrent(Math.round(Math.abs(trackRef.current!.scrollLeft) / getSlideWidth()));
  };

  const handleArrowClick = (step: 1 | -1) => (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setAnnounce(true);

    if (step === 1 && current >= total - 1) {
      scrollToIndex(0, true);
      return;
    }
    if (step === -1 && current === 0) {
      scrollToIndex(total - 1, true);
      return;
    }
    scrollToIndex(current + step);
  };

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    suppressClick.current = false;
    if (e.pointerType !== "mouse" || e.button !== 0) return; // touch and pen keep native scrolling
    drag.current = { x: e.clientX, left: e.currentTarget.scrollLeft, moved: false };
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state) return;

    const dx = e.clientX - state.x;
    if (!state.moved) {
      if (Math.abs(dx) < DRAG_START_PX) return;
      state.moved = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      e.currentTarget.style.scrollSnapType = "none"; // snapping would fight the drag
      e.currentTarget.style.cursor = "grabbing";
    }
    e.currentTarget.scrollLeft = state.left - dx;
  };

  const handlePointerEnd = (e: PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    drag.current = null;
    if (!state?.moved) return;

    const track = e.currentTarget;
    suppressClick.current = true; // a drag must not open the car page
    track.style.cursor = "";

    const width = getSlideWidth();
    const startIndex = Math.round(Math.abs(state.left) / width);
    const dragged = Math.abs(track.scrollLeft) - Math.abs(state.left);
    const step = dragged > width * DRAG_CHANGE_RATIO ? 1 : dragged < -width * DRAG_CHANGE_RATIO ? -1 : 0;
    scrollToIndex(Math.min(total - 1, Math.max(0, startIndex + step)));

    const restoreSnap = () => {
      track.style.scrollSnapType = "";
    };
    track.addEventListener("scrollend", restoreSnap, { once: true });
    setTimeout(restoreSnap, 600); // scrollend isn't supported everywhere
  };

  const handleClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <div
        ref={trackRef}
        tabIndex={-1} // keep the tab order link -> arrows; Chrome otherwise makes the scroller a tab stop
        className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain rounded-3xl [scrollbar-width:none] md:cursor-grab [&::-webkit-scrollbar]:hidden"
        onScroll={handleScroll}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onClickCapture={handleClickCapture}
        onDragStart={(e) => e.preventDefault()}
      >
        {images.map((image, index) => (
          <div key={index} className="h-60 w-full shrink-0 snap-start snap-always rounded-3xl bg-secondary-light">
            {hasImage(index) ? (
              <CarCardImage
                index={index}
                mediaUrl={image.mediaUrl}
                blurPlaceholder={DEFAULT_PLACEHOLDER}
                makeModel={makeModel}
                priority={index === 0 && priority}
                // lazy: Chrome preloads it only for cards near the screen, so swipes are instant without
                // downloading neighbour photos for all 20 cards
                loading={index === 0 ? undefined : "lazy"}
              />
            ) : null}
          </div>
        ))}
      </div>

      <span className="sr-only" aria-live="polite">
        {announce ? `Photo ${current + 1} of ${total}` : ""}
      </span>

      <div className={`${arrowClassName} bottom-11`}>
        <button type="button" aria-label="Show previous image" onClick={handleArrowClick(-1)}>
          <IconArrowRight fill="hsla(var(--secondary-foreground))" width="15" height="15" className="-rotate-180" />
        </button>
      </div>
      <div className={`${arrowClassName} bottom-0`}>
        <button type="button" aria-label="Show next image" onClick={handleArrowClick(1)}>
          <IconArrowRight fill="hsla(var(--secondary-foreground))" width="15" height="15" />
        </button>
      </div>
    </>
  );
}
